import { useEffect, useRef, useState } from "react";
import {
  Loader2, Save, ExternalLink, Download, QrCode, Globe, Copy, Check, Image as ImageIcon,
  Upload, Rocket, Palette, Layout as LayoutIcon, ChevronUp, ChevronDown, Plus, Trash2,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import ShareButtons from "@/components/minisite/ShareButtons";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

const SECTION_META = {
  stats: { label: "Statistik", note: "Angka properti, kamar tersedia, area, dan rating (otomatis).", editable: false },
  about: { label: "Tentang Kami", note: "Isi teks diambil dari kolom 'Tentang / Deskripsi' di atas.", titleOnly: true },
  featured: { label: "Hunian Pilihan", note: "Menampilkan properti unggulan Anda (otomatis)." },
  reviews: { label: "Ulasan Penghuni", note: "Menampilkan ulasan asli penghuni (otomatis)." },
  cta: { label: "Ajakan & Kontak (CTA)", note: "Tombol WhatsApp & booking di bagian bawah." },
};

export default function OwnerMiniSitePage() {
  const [form, setForm] = useState({ slug: "", headline: "", tagline: "", about: "", service_area: "", instagram: "", website: "" });
  const [theme, setTheme] = useState({ accent_color: "" });
  const [hero, setHero] = useState({ cta_text: "", overlay: 0.55 });
  const [sections, setSections] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [savedSlug, setSavedSlug] = useState(null);
  const [count, setCount] = useState(0);
  const [statusFlags, setStatusFlags] = useState({ is_published: false, has_unpublished_changes: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState("");
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  const logoInput = useRef(null);
  const coverInput = useRef(null);

  const origin = window.location.origin;
  const publicUrl = savedSlug ? `${origin}/properti/${savedSlug}` : null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyData = (d) => {
    setForm({
      slug: d.slug || "", headline: d.headline || "", tagline: d.tagline || "",
      about: d.about || "", service_area: d.service_area || "", instagram: d.instagram || "", website: d.website || "",
    });
    setTheme({ accent_color: d.theme?.accent_color || "" });
    setHero({ cta_text: d.hero?.cta_text || "", overlay: d.hero?.overlay ?? 0.55 });
    setSections(d.sections || []);
    setSavedSlug(d.slug || null);
    setLogoUrl(d.logo_url || null);
    setCoverUrl(d.cover_url || null);
    setCount(d.published_property_count || 0);
    setStatusFlags({ is_published: d.is_published, has_unpublished_changes: d.has_unpublished_changes });
  };

  const load = () => apiClient.get("/owner/minisite").then((r) => applyData(r.data || {}));
  useEffect(() => { load().catch(() => {}).finally(() => setLoading(false)); }, []);

  const buildPayload = () => ({
    slug: form.slug.trim().toLowerCase(),
    headline: form.headline.trim() || null,
    tagline: form.tagline.trim() || null,
    about: form.about.trim() || null,
    service_area: form.service_area.trim() || null,
    instagram: form.instagram.trim() || null,
    website: form.website.trim() || null,
    theme: { accent_color: theme.accent_color || null },
    hero: { cta_text: hero.cta_text.trim() || null, overlay: Number(hero.overlay) },
    sections: sections.map((s, i) => ({ ...s, order: i })),
  });

  const save = async () => {
    if (!form.slug.trim()) { toast.error("Slug wajib diisi."); return; }
    setSaving(true);
    try {
      const { data } = await apiClient.put("/owner/minisite", buildPayload());
      applyData(data);
      toast.success("Draft web-site tersimpan.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!form.slug.trim()) { toast.error("Slug wajib diisi."); return; }
    setPublishing(true);
    try {
      await apiClient.put("/owner/minisite", buildPayload());
      const { data } = await apiClient.post("/owner/minisite/publish");
      applyData(data);
      toast.success("Web-site berhasil dipublish.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await apiClient.post(`/owner/minisite/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "logo") setLogoUrl(data.url); else setCoverUrl(data.url);
      setStatusFlags((s) => ({ ...s, has_unpublished_changes: true }));
      toast.success(`${kind === "logo" ? "Logo" : "Cover"} diunggah. Jangan lupa Publish.`);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const setSection = (i, k, v) => setSections((arr) => { const s = [...arr]; s[i] = { ...s[i], [k]: v }; return s; });
  const moveSection = (i, dir) => setSections((arr) => {
    const s = [...arr]; const j = i + dir; if (j < 0 || j >= s.length) return arr;
    [s[i], s[j]] = [s[j], s[i]]; return s;
  });
  const addCustom = () => setSections((arr) => [...arr, {
    key: `custom-${Math.random().toString(36).slice(2, 10)}`, title: "Bagian Baru", subtitle: null,
    content: "", enabled: true, order: arr.length, type: "custom",
  }]);
  const removeSection = (i) => setSections((arr) => arr.filter((_, idx) => idx !== i));

  const copyUrl = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Gagal menyalin tautan."); }
  };
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-minisite-${savedSlug}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <OwnerLayout testid="owner-minisite-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Web-Site</h1>
        {statusFlags.has_unpublished_changes && (
          <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="minisite-status">
            Ada perubahan belum dipublish
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Atur sendiri tampilan web-site Anda — branding, hero, warna, dan susunan section. Simpan sebagai draft, lalu Publish saat siap.
      </p>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            {/* Branding */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2"><ImageIcon size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Identitas & Branding</h2></div>
              <div className="grid grid-cols-2 gap-4">
                <BrandingUpload label="Logo" url={logoUrl} busy={uploading === "logo"} inputRef={logoInput}
                  aspect="aspect-square" testid="minisite-logo" onPick={() => logoInput.current?.click()} onFile={(f) => upload("logo", f)} />
                <BrandingUpload label="Cover / Banner Hero" url={coverUrl} busy={uploading === "cover"} inputRef={coverInput}
                  aspect="aspect-video" testid="minisite-cover" onPick={() => coverInput.current?.click()} onFile={(f) => upload("cover", f)} />
              </div>
              <Field label="Judul / Nama Bisnis (judul hero)">
                <input value={form.headline} onChange={set("headline")} data-testid="minisite-headline-input" placeholder="Contoh: QQ Kost Group" className={inputCls} />
              </Field>
              <Field label="Tagline / Subjudul Hero">
                <input value={form.tagline} onChange={set("tagline")} data-testid="minisite-tagline-input" placeholder="Kos nyaman & strategis dekat kampus" className={inputCls} />
              </Field>
              <Field label="Area Layanan (opsional)">
                <input value={form.service_area} onChange={set("service_area")} data-testid="minisite-area-input" placeholder="Contoh: Surakarta & sekitarnya" className={inputCls} />
              </Field>
              <Field label="Tentang / Deskripsi (mengisi section 'Tentang Kami')">
                <textarea value={form.about} onChange={set("about")} rows={3} data-testid="minisite-about-input" placeholder="Ceritakan tentang bisnis kos Anda..." className={`${inputCls} resize-none`} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Instagram (opsional)"><input value={form.instagram} onChange={set("instagram")} data-testid="minisite-instagram-input" placeholder="@username" className={inputCls} /></Field>
                <Field label="Website (opsional)"><input value={form.website} onChange={set("website")} data-testid="minisite-website-input" placeholder="https://..." className={inputCls} /></Field>
              </div>
            </div>

            {/* Tema & Hero */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="minisite-theme-card">
              <div className="flex items-center gap-2"><Palette size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Tema & Hero</h2></div>
              <Field label="Warna Aksen (tombol & label)">
                <div className="flex items-center gap-2">
                  <input type="color" value={theme.accent_color || "#e07a5f"} onChange={(e) => setTheme({ accent_color: e.target.value })} data-testid="minisite-accent-color" className="w-10 h-10 rounded-lg border border-border bg-background cursor-pointer" />
                  <input className={inputCls} value={theme.accent_color || ""} placeholder="#e07a5f (kosongkan = default)" onChange={(e) => setTheme({ accent_color: e.target.value })} />
                  {theme.accent_color && <button type="button" onClick={() => setTheme({ accent_color: "" })} className="text-xs text-muted-foreground hover:text-foreground px-2" data-testid="minisite-accent-clear">Reset</button>}
                </div>
              </Field>
              <Field label="Teks Tombol Hero">
                <input value={hero.cta_text} onChange={(e) => setHero((h) => ({ ...h, cta_text: e.target.value }))} data-testid="minisite-hero-cta" placeholder="Lihat Semua Hunian" className={inputCls} />
              </Field>
              <Field label={`Kegelapan Gambar Hero (${Math.round((hero.overlay ?? 0.55) * 100)}%)`}>
                <input type="range" min={0} max={0.9} step={0.05} value={hero.overlay ?? 0.55} onChange={(e) => setHero((h) => ({ ...h, overlay: parseFloat(e.target.value) }))} data-testid="minisite-hero-overlay" className="w-full accent-primary" />
                <p className="text-[11px] text-muted-foreground mt-1">Makin tinggi, teks hero makin mudah terbaca di atas gambar.</p>
              </Field>
            </div>

            {/* Sections */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="minisite-sections-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><LayoutIcon size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Susunan Section</h2></div>
                <button onClick={addCustom} data-testid="minisite-add-section" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/5"><Plus size={14} /> Tambah Section</button>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">Aktif/nonaktifkan, ubah judul, atur urutan, atau tambah section teks sendiri.</p>
              <div className="space-y-3">
                {sections.map((s, i) => {
                  const meta = SECTION_META[s.key];
                  const isCustom = s.type === "custom" || !meta;
                  return (
                    <div key={s.key} className="rounded-xl border border-border p-4" data-testid={`minisite-section-${s.key}`}>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="font-heading font-semibold text-sm">{meta ? meta.label : (s.title || "Section Kustom")}{isCustom && <span className="ml-2 text-[10px] uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">custom</span>}</span>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary/60" data-testid={`minisite-section-up-${s.key}`}><ChevronUp size={15} /></button>
                          <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="p-1.5 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary/60" data-testid={`minisite-section-down-${s.key}`}><ChevronDown size={15} /></button>
                          <Toggle checked={s.enabled} onChange={(v) => setSection(i, "enabled", v)} testid={`minisite-section-toggle-${s.key}`} />
                          {isCustom && <button onClick={() => removeSection(i)} className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`minisite-section-remove-${s.key}`}><Trash2 size={14} /></button>}
                        </div>
                      </div>
                      {meta?.note && <p className="text-[11px] text-muted-foreground mb-2">{meta.note}</p>}
                      {(!meta || !meta.editable === false) && meta?.editable === false ? null : (
                        <div className="space-y-2">
                          {!(meta && meta.editable === false) && (
                            <input className={inputCls} placeholder="Judul section" value={s.title || ""} onChange={(e) => setSection(i, "title", e.target.value)} data-testid={`minisite-section-title-${s.key}`} />
                          )}
                          {isCustom ? (
                            <textarea rows={4} className={`${inputCls} resize-y`} placeholder="Isi teks section (mis. fasilitas unggulan, aturan kos)..." value={s.content || ""} onChange={(e) => setSection(i, "content", e.target.value)} data-testid={`minisite-section-content-${s.key}`} />
                          ) : (meta && !meta.titleOnly && meta.editable !== false && (
                            <input className={inputCls} placeholder="Subjudul (opsional)" value={s.subtitle || ""} onChange={(e) => setSection(i, "subtitle", e.target.value)} data-testid={`minisite-section-subtitle-${s.key}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Slug + actions */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2"><Globe size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Alamat Web-Site</h2></div>
              <Field label="Slug (alamat web-site)">
                <div className="flex items-center rounded-xl border border-border bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                  <span className="pl-3 pr-1 text-sm text-muted-foreground whitespace-nowrap">/properti/</span>
                  <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))} data-testid="minisite-slug-input" placeholder="qqkost-muwardi" className="flex-1 bg-transparent py-2.5 pr-3 text-sm outline-none" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Huruf kecil, angka, dan tanda "-".</p>
              </Field>
              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={save} disabled={saving} data-testid="minisite-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 disabled:opacity-60">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Draft
                </button>
                <button onClick={publish} disabled={publishing} data-testid="minisite-publish" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
                  {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Publish
                </button>
                {publicUrl && (
                  <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="minisite-preview" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border font-semibold hover:bg-secondary/60 transition-colors">
                    <ExternalLink size={16} /> Preview
                  </a>
                )}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="minisite-count">{count} properti berstatus published akan tampil di web-site Anda.</p>
            </div>

            {publicUrl && (
              <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                <Field label="Tautan publik">
                  <div className="flex items-center gap-2">
                    <input readOnly value={publicUrl} data-testid="minisite-url" className="flex-1 rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none" />
                    <button onClick={copyUrl} data-testid="minisite-copy" className="grid place-items-center w-11 h-11 shrink-0 rounded-xl border border-border hover:bg-secondary/60 transition-colors">
                      {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                    </button>
                  </div>
                </Field>
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Bagikan web-site</p>
                  <ShareButtons url={publicUrl} title="Web-Site Saya — AVASA Coliving" />
                </div>
              </div>
            )}
          </div>

          {/* QR */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-2xl p-6 text-center sticky top-24">
              <div className="flex items-center justify-center gap-2 mb-4"><QrCode size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">QR Code</h2></div>
              {publicUrl ? (
                <>
                  <div ref={qrRef} className="inline-block bg-white p-4 rounded-2xl border border-border" data-testid="minisite-qr">
                    <QRCodeCanvas value={publicUrl} size={200} level="M" includeMargin={false} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 mb-4">Tempel di depan kamar, resepsionis, atau brosur. Scan langsung membuka web-site Anda.</p>
                  <button onClick={downloadQr} data-testid="minisite-qr-download" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
                    <Download size={16} /> Download PNG
                  </button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground py-10" data-testid="minisite-qr-empty">Simpan slug terlebih dahulu untuk membuat QR Code.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}

function Field({ label, children }) {
  return (<div><label className="block text-xs text-muted-foreground mb-1">{label}</label>{children}</div>);
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function BrandingUpload({ label, url, busy, inputRef, aspect, onPick, onFile, testid }) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <button type="button" onClick={onPick} data-testid={`${testid}-upload`}
        className={`relative w-full ${aspect} rounded-xl border border-dashed border-border bg-secondary/30 overflow-hidden grid place-items-center hover:border-primary/50 transition-colors`}>
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <span className="flex flex-col items-center gap-1 text-muted-foreground text-xs"><Upload size={18} /> Unggah {label}</span>
        )}
        {busy && <span className="absolute inset-0 grid place-items-center bg-black/30"><Loader2 size={20} className="animate-spin text-white" /></span>}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}
